import React from 'react';
import { ChevronLeft, ChevronRight, RefreshCcw } from 'lucide-react';

import RunRow from './RunRow';

const RunTableSection = ({ controller }) => {
  const {
    runModelSearchQuery,
    setRunModelSearchQuery,
    runDatasetSearchQuery,
    setRunDatasetSearchQuery,
    setExpandedRun,
    filteredActiveRuns,
    getRunProgressMetrics,
    formatStartedAt,
    formatResizeDisplay,
    formatEta,
    pagedDisplayedTestResults,
    indexOfFirstRun,
    loading,
    dataLoadError,
    displayedTestResults,
    activeRuns,
    runSearchSummary,
    filteredDisplayedTestResults,
    itemsPerPage,
    showingFirstRunNumber,
    showingLastRunNumber,
    currentPage,
    totalPages,
    setCurrentPage,
    setItemsPerPage,
    paginate,
  } = controller;

  return (
    <>
      <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <h3 className="text-xl font-semibold text-ink">Recent Test Runs</h3>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
          <input
            value={runModelSearchQuery}
            onChange={(event) => {
              setRunModelSearchQuery(event.target.value);
              setExpandedRun(null);
            }}
            placeholder="Filter by model..."
            className="w-full sm:w-56 bg-surface border border-hairline rounded px-3 py-1.5 outline-none focus:border-accent text-sm text-ink placeholder:text-muted"
          />
          <input
            value={runDatasetSearchQuery}
            onChange={(event) => {
              setRunDatasetSearchQuery(event.target.value);
              setExpandedRun(null);
            }}
            placeholder="Filter by dataset..."
            className="w-full sm:w-56 bg-surface border border-hairline rounded px-3 py-1.5 outline-none focus:border-accent text-sm text-ink placeholder:text-muted"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[1460px] text-left border-collapse">
          <thead>
            <tr className="bg-surface-2 border-b border-hairline">
              <th className="p-4 font-semibold text-muted"></th>
              <th className="p-4 font-semibold text-muted">Timestamp</th>
              <th className="p-4 font-semibold text-muted">Model</th>
              <th className="p-4 font-semibold text-muted">Resize</th>
              <th className="p-4 font-semibold text-muted">Compression</th>
              <th className="p-4 font-semibold text-muted">eBay RAG</th>
              <th className="p-4 font-semibold text-muted">Notes RAG</th>
              <th className="p-4 font-semibold text-muted">Dataset</th>
              <th className="p-4 font-semibold text-muted">Mode</th>
              <th className="p-4 font-semibold text-muted">Data Points</th>
              <th className="p-4 font-semibold text-muted">Avg Speed</th>
              <th className="p-4 font-semibold text-muted">Accuracy</th>
              <th className="p-4 font-semibold text-muted">Input Tokens</th>
              <th className="p-4 font-semibold text-muted">Output Tokens</th>
              <th className="p-4 font-semibold text-muted">Cost</th>
            </tr>
          </thead>
          <tbody>
            {filteredActiveRuns.map((runningRun, ridx) => {
              const progress = getRunProgressMetrics(runningRun);
              const processed = progress.total !== null
                ? `${progress.attempted}/${progress.total}${progress.datasetTruncated ? ' (truncated)' : ''}`
                : `${progress.attempted}/?`;
              const progressHint = `Success ${progress.successful}, skipped ${progress.skipped}, ETA ${progress.attempted > 0 ? formatEta(progress.etaSeconds) : 'N/A'}`;
              return (
                <tr
                  key={`running-${runningRun.run_id || runningRun.pid || ridx}`}
                  className="border-b border-accent/20 bg-accent/5"
                >
                  <td className="p-4 text-sm text-accent">
                    <RefreshCcw size={16} className="animate-spin" />
                  </td>
                  <td className="p-4 text-sm text-accent font-medium">{formatStartedAt(runningRun.started_at)}</td>
                  <td className="p-4 text-sm text-accent">{runningRun.model || 'N/A'}</td>
                  <td className="p-4 text-sm text-accent">{formatResizeDisplay(runningRun.resize_mode)}</td>
                  <td className="p-4 text-sm text-accent">{runningRun.compression_mode || 'N/A'}</td>
                  <td className="p-4 text-sm text-accent">{runningRun.use_ebay_rag ? 'On' : 'Off'}</td>
                  <td className="p-4 text-sm text-accent">{runningRun.use_notes_rag ? 'On' : 'Off'}</td>
                  <td className="p-4 text-sm text-accent">
                    {runningRun.dataset_name || runningRun.dataset_prefix || 'Container root'}
                  </td>
                  <td className="p-4 text-sm">
                    <span className="px-2 py-1 rounded-full text-xs font-bold bg-accent/10 text-accent">
                      RUNNING {progress.progressPercent.toFixed(0)}%
                    </span>
                  </td>
                  <td className="p-4 text-sm text-accent" title={progressHint}>{processed}</td>
                  <td className="p-4 text-sm text-accent">
                    {progress.avgDurationSeconds ? `${(progress.avgDurationSeconds * 1000).toFixed(0)} ms` : 'N/A'}
                  </td>
                  <td className="p-4 text-sm text-accent">-</td>
                  <td className="p-4 text-sm text-accent">-</td>
                  <td className="p-4 text-sm text-accent">-</td>
                  <td className="p-4 text-sm text-accent">-</td>
                </tr>
              );
            })}

            {pagedDisplayedTestResults.map((run, idx) => {
              const absoluteIdx = indexOfFirstRun + idx;
              const runRowKey = `${String(run?.run_id || 'run')}-${String(run?.timestamp || 'ts')}-${absoluteIdx}`;
              return (
                <RunRow
                  key={runRowKey}
                  controller={controller}
                  run={run}
                  absoluteIdx={absoluteIdx}
                  runRowKey={runRowKey}
                />
              );
            })}

            {loading && (
              <tr>
                <td colSpan="15" className="p-8 text-center text-muted italic">Loading test runs...</td>
              </tr>
            )}
            {!loading && dataLoadError && (
              <tr>
                <td colSpan="15" className="p-8 text-center text-danger italic">
                  Failed to load test runs: {dataLoadError}
                </td>
              </tr>
            )}
            {!loading && !dataLoadError && displayedTestResults.length === 0 && activeRuns.length === 0 && (
              <tr>
                <td colSpan="15" className="p-8 text-center text-muted italic">No test runs found yet.</td>
              </tr>
            )}
            {!loading && !dataLoadError && (displayedTestResults.length > 0 || activeRuns.length > 0) && filteredDisplayedTestResults.length === 0 && filteredActiveRuns.length === 0 && (
              <tr>
                <td colSpan="15" className="p-8 text-center text-muted italic">
                  No test runs match {runSearchSummary || 'the current filters'}.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {filteredDisplayedTestResults.length > itemsPerPage && (
        <div className="flex flex-col sm:flex-row justify-between items-center pt-6 border-t border-hairline gap-4">
          <div className="text-sm text-muted">
            Showing <span className="font-medium">{showingFirstRunNumber}</span> to <span className="font-medium">{showingLastRunNumber}</span> of <span className="font-medium">{filteredDisplayedTestResults.length}</span> runs
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center mr-4">
              <span className="text-sm text-muted mr-2">Show:</span>
              <select
                value={itemsPerPage}
                onChange={(event) => {
                  setItemsPerPage(Number(event.target.value));
                  setCurrentPage(1);
                  setExpandedRun(null);
                }}
                className="text-sm bg-surface text-ink border border-hairline rounded px-2 py-1 outline-none focus:border-accent"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
            </div>
            <nav className="flex items-center gap-1">
              <button
                onClick={() => paginate(currentPage - 1)}
                disabled={currentPage === 1}
                className={`p-2 rounded border ${currentPage === 1 ? 'text-muted/40 border-hairline cursor-not-allowed' : 'text-muted border-hairline hover:bg-surface-2'}`}
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => page === 1 || page === totalPages || Math.abs(page - currentPage) <= 1)
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && <span className="px-2 text-muted">...</span>}
                    <button
                      onClick={() => paginate(page)}
                      className={`min-w-[32px] h-8 rounded text-sm font-medium transition-colors ${currentPage === page ? 'bg-brand text-white' : 'text-muted hover:bg-surface-2'}`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}
              <button
                onClick={() => paginate(currentPage + 1)}
                disabled={currentPage === totalPages}
                className={`p-2 rounded border ${currentPage === totalPages ? 'text-muted/40 border-hairline cursor-not-allowed' : 'text-muted border-hairline hover:bg-surface-2'}`}
              >
                <ChevronRight size={16} />
              </button>
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default RunTableSection;
